/**
 * DocumentUploadPage — Public document upload page
 *
 * Route: /upload/:token  (no auth required)
 * Client uploads ID, bank statement, or proof of residence.
 * F-132
 */

import { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Loader2, CheckCircle2, AlertTriangle, Upload, FileText } from 'lucide-react';
import { loadRuntimeConfig } from '../config/runtime-config';

type PageState = 'loading' | 'invalid' | 'expired' | 'used' | 'form' | 'uploading' | 'done' | 'error';

export function DocumentUploadPage({ token }: { token: string }) {
  const [state, setState] = useState<PageState>('loading');
  const [documentLabel, setDocumentLabel] = useState('Document');
  const [acceptedFormats, setAcceptedFormats] = useState<string[]>(['PDF', 'JPG', 'PNG']);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadRuntimeConfig()
      .then(cfg => {
        const base = cfg.apiGateway.baseUrl;
        setBaseUrl(base);
        return fetch(`${base}/public/upload/${token}`);
      })
      .then(r => r.json())
      .then(json => {
        if (!json.success) {
          const msg = json.message || '';
          if (msg.includes('expired')) setState('expired');
          else if (msg.includes('already been used')) setState('used');
          else { setErrorMsg(msg); setState('invalid'); }
          return;
        }
        setDocumentLabel(json.data.documentLabel || 'Document');
        setAcceptedFormats(json.data.acceptedFormats || ['PDF', 'JPG', 'PNG']);
        setState('form');
      })
      .catch(() => { setErrorMsg('Could not load this page. Please try again.'); setState('error'); });
  }, [token]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file && file.size > 10 * 1024 * 1024) {
      setErrorMsg('File too large. Maximum 10MB.');
      setSelectedFile(null);
      return;
    }
    setErrorMsg('');
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) { setErrorMsg('Please select a file to upload'); return; }

    setState('uploading');
    setErrorMsg('');

    try {
      // Convert to base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1]); // strip data:...;base64, prefix
        };
        reader.onerror = reject;
        reader.readAsDataURL(selectedFile);
      });

      const res = await fetch(`${baseUrl}/public/upload/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileBase64: base64,
          fileName: selectedFile.name,
          mimeType: selectedFile.type,
        }),
      });
      const json = await res.json();
      if (json.success) {
        setState('done');
      } else {
        setErrorMsg(json.message || 'Upload failed. Please try again.');
        setState('form');
      }
    } catch {
      setErrorMsg('Upload failed. Please check your connection and try again.');
      setState('form');
    }
  };

  if (state === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (state === 'expired') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold mb-2">Link Expired</h1>
          <p className="text-gray-600">This upload link has expired. Please contact your agent.</p>
        </div>
      </div>
    );
  }

  if (state === 'used') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold mb-2">Already Uploaded</h1>
          <p className="text-gray-600">This document has already been uploaded. Contact your agent if you need to resubmit.</p>
        </div>
      </div>
    );
  }

  if (state === 'done') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold mb-2">Upload Successful</h1>
          <p className="text-gray-600">Thank you. Your {documentLabel} has been received.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-8 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Upload {documentLabel}</h1>
          <p className="text-gray-500 mt-1 text-sm">
            Accepted formats: {acceptedFormats.join(', ')} · Max 10MB
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border p-6 space-y-5">
          {/* Drop zone */}
          <div
            className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-colors"
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileChange}
            />
            {selectedFile ? (
              <div className="flex items-center justify-center gap-3">
                <FileText className="w-8 h-8 text-blue-500" />
                <div className="text-left">
                  <p className="font-medium text-gray-800 text-sm">{selectedFile.name}</p>
                  <p className="text-xs text-gray-500">{(selectedFile.size / (1024 * 1024)).toFixed(2)} MB</p>
                </div>
              </div>
            ) : (
              <div>
                <Upload className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-sm font-medium text-gray-600">Click to select a file</p>
                <p className="text-xs text-gray-400 mt-1">or drag and drop</p>
              </div>
            )}
          </div>

          {errorMsg && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {errorMsg}
            </p>
          )}

          <Button
            className="w-full"
            disabled={!selectedFile || state === 'uploading'}
            onClick={handleUpload}
          >
            {state === 'uploading' ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading…</>
            ) : (
              <><Upload className="w-4 h-4 mr-2" /> Upload Document</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
